/**
 * AuthorAvatars — renders a row of circular author avatars for a blog post.
 *
 * Accepts the raw frontmatter `author` string (which may contain multiple
 * authors), resolves each to a canonical author via utils/authors, and renders:
 *   - a GatsbyImage avatar if `src/assets/authors/<slug>.{png,jpg,jpeg,webp}` exists
 *   - otherwise an initials placeholder with a stable per-author color
 *
 * Avatars overlap slightly when there's more than one author. Names are shown
 * alongside by default (set showNames={false} for an avatars-only row).
 */
import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import React from "react"

import { parseAuthors } from "utils/authors"
import * as css from "./AuthorAvatars.module.css"

function AuthorAvatars({ author, showNames = true, size = 32 }) {
  // Build-time list of available avatar files in src/assets/authors/.
  // Mirrors the usePageAssets pattern so existence is known at build time
  // (no runtime 404s) and images get childImageSharp optimization.
  const data = useStaticQuery(graphql`
    query AuthorAvatarsQuery {
      avatars: allFile(
        filter: {
          sourceInstanceName: { eq: "images" }
          relativeDirectory: { eq: "authors" }
          extension: { in: ["png", "jpg", "jpeg", "webp"] }
        }
      ) {
        nodes {
          name
          childImageSharp {
            gatsbyImageData(
              width: 96
              height: 96
              transformOptions: { cropFocus: CENTER, fit: COVER }
              placeholder: BLURRED
            )
          }
        }
      }
    }
  `)

  const authors = parseAuthors(author)
  if (authors.length === 0) return null

  const avatarBySlug = new Map(data.avatars.nodes.map((n) => [n.name, n]))

  return (
    <div className={css.root} style={{ "--avatar-size": `${size}px` }}>
      <div className={css.avatars}>
        {authors.map((a, i) => {
          const node = avatarBySlug.get(a.slug)
          const image = node ? getImage(node.childImageSharp) : null
          return (
            <span
              key={a.slug}
              className={css.avatar}
              title={a.name}
              // Earlier authors stack above later ones for a clean overlap.
              style={{ zIndex: authors.length - i }}
            >
              {image ? (
                <GatsbyImage image={image} alt={a.name} className={css.image} />
              ) : (
                <span
                  className={css.initials}
                  style={{ backgroundColor: a.color }}
                  role="img"
                  aria-label={a.name}
                >
                  {a.initials}
                </span>
              )}
            </span>
          )
        })}
      </div>
      {showNames && (
        <span className={css.names}>
          {authors.map((a) => a.name).join(", ")}
        </span>
      )}
    </div>
  )
}

export default AuthorAvatars
